import React from "react";
import { Box, TextInput, ActionIcon, Loader } from "@mantine/core";
import { L2KBadge } from "../ui/Typography";
import { useMediaQuery } from "@mantine/hooks";
import {
  IconSearch,
  IconX,
  IconAdjustmentsHorizontal,
} from "@tabler/icons-react";
import { useHeaderVisibility } from "../../context/HeaderVisibilityContext";

export interface SearchFilterBarProps {
  /**
   * Search input value
   */
  searchValue: string;
  /**
   * Search input onChange handler
   */
  onSearchChange: (value: string) => void;
  /**
   * Placeholder text for search input
   */
  searchPlaceholder?: string;
  /**
   * Filter button click handler
   */
  onFilterClick: () => void;
  /**
   * Active filter count to display on badge (0 means no badge)
   */
  activeFilterCount?: number;
  /**
   * Whether search is currently debouncing (shows loader)
   */
  debouncing?: boolean;
  /**
   * Size variant
   * - "compact": Smaller padding and spacing (like DanceDictionary)
   * - "spacious": Larger padding and spacing (like Videos)
   */
  variant?: "compact" | "spacious";
  /**
   * Sticky top position (defaults based on variant)
   */
  stickyTop?: string;
  /**
   * Z-index for sticky positioning (defaults based on variant)
   */
  zIndex?: number;
  /**
   * Custom CSS styles to override defaults
   */
  style?: React.CSSProperties;
  /**
   * Custom className
   */
  className?: string;
  /**
   * Filter button icon size
   */
  filterIconSize?: number;
  /**
   * Filter button size ("lg", "xl", etc.)
   */
  filterButtonSize?: "xs" | "sm" | "md" | "lg" | "xl";
  /**
   * Search icon size
   */
  searchIconSize?: number;
  /**
   * Accessibility label for filter button
   */
  filterButtonAriaLabel?: string;
}

/**
 * SearchFilterBar - Reusable search and filter bar component
 *
 * Features:
 * - Sticky positioning
 * - Search input with clear button
 * - Filter button with badge count
 * - Debouncing loader indicator
 * - Variant-based styling (compact/spacious)
 * - Full customization via props
 *
 * @example
 * <SearchFilterBar
 *   searchValue={searchTerm}
 *   onSearchChange={setSearchTerm}
 *   onFilterClick={toggleFilterDrawer}
 *   activeFilterCount={5}
 *   debouncing={debouncingSearch}
 *   variant="compact"
 * />
 */
export function SearchFilterBar({
  searchValue,
  onSearchChange,
  searchPlaceholder = "Search...",
  onFilterClick,
  activeFilterCount = 0,
  debouncing = false,
  variant = "compact",
  stickyTop,
  zIndex,
  style,

  filterIconSize,
  filterButtonSize,
  searchIconSize = 16,
  filterButtonAriaLabel = "Open filters",
}: SearchFilterBarProps) {
  // Detect if we're on desktop (sm breakpoint and above = 640px)
  const isDesktop = useMediaQuery("(min-width: 640px)");

  // Get header visibility from context (if available)
  const headerVisibility = useHeaderVisibility();

  // Header heights based on breakpoints
  const HEADER_HEIGHT_MOBILE = 56; // base breakpoint
  const HEADER_HEIGHT_DESKTOP = 60; // sm breakpoint and above
  const HEADER_OFFSET_PADDING = 8; // Fixed padding after header

  // Variant-based defaults
  const variantStyles = {
    compact: {
      top: "8px", // Mobile base position (will adjust if header visible)
      zIndex: 5,
      backgroundColor: "#18181B90",
      padding: "8px",
      borderRadius: "8px",
      // Remove horizontal margins on desktop - parent Stack already has padding
      // On mobile, use negative left/right to extend beyond parent padding
      margin: isDesktop ? "0px 0px 1rem 0px" : "0px 0px 1rem 0px",
      left: isDesktop ? undefined : "-16px",
      right: isDesktop ? undefined : "-16px",
      filterButtonSize: "lg" as const,
      filterIconSize: 16,
    },
    spacious: {
      top: "12px", // Mobile base position (will adjust if header visible)
      zIndex: 10,
      backgroundColor: "#18181B",
      padding: "16px",
      borderRadius: "12px",
      // Remove horizontal margins on desktop - parent Stack already has padding
      // On mobile, use negative left/right to extend beyond parent padding
      margin: isDesktop ? "12px 0px 1.5rem 0px" : "12px 0px 1.5rem 0px",
      left: isDesktop ? undefined : "-16px",
      right: isDesktop ? undefined : "-16px",
      filterButtonSize: "xl" as const,
      filterIconSize: undefined,
    },
  };

  const variantStyle = variantStyles[variant];

  // Calculate top position:
  // - Desktop: account for header height + padding (always visible)
  // - Mobile: dynamically adjust based on header visibility if context available
  //   Otherwise, use variant-based values
  let calculatedTop: string;
  if (stickyTop) {
    calculatedTop = stickyTop;
  } else if (isDesktop) {
    calculatedTop = `${HEADER_HEIGHT_DESKTOP + HEADER_OFFSET_PADDING}px`;
  } else if (headerVisibility) {
    // Mobile with hideOnScroll: adjust position based on header visibility
    const baseTop = parseInt(variantStyle.top);
    calculatedTop = headerVisibility.headerVisible
      ? `${HEADER_HEIGHT_MOBILE + HEADER_OFFSET_PADDING}px`
      : `${baseTop}px`;
  } else {
    // Mobile without hideOnScroll: use variant-based values
    calculatedTop = variantStyle.top;
  }

  // Merge variant styles with custom overrides
  // Fix horizontal scrolling by ensuring proper width constraints
  // For sticky positioning, use left/right instead of negative margins to avoid positioning issues
  const containerStyle: React.CSSProperties = {
    position: "sticky",
    top: calculatedTop,
    left: variantStyle.left,
    right: variantStyle.right,
    zIndex: zIndex ?? variantStyle.zIndex,
    backgroundColor: variantStyle.backgroundColor,
    padding: variantStyle.padding,
    marginBottom: "1.5rem",
    borderRadius: variantStyle.borderRadius,
    margin: variantStyle.margin,
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4), 0 2px 8px rgba(0, 0, 0, 0.2)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    backdropFilter: "blur(8px)",
    width: variantStyle.left !== undefined ? "100%" : "100%", // Full width when using left/right
    maxWidth: "100%",
    boxSizing: "border-box",
    transition: headerVisibility
      ? "top 300ms cubic-bezier(0.4, 0, 0.2, 1)"
      : undefined,
    ...style,
  };

  const finalFilterButtonSize =
    filterButtonSize ?? variantStyle.filterButtonSize;
  const finalFilterIconSize = filterIconSize ?? variantStyle.filterIconSize;

  return (
    <Box style={containerStyle}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "16px",
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
        }}
      >
        <TextInput
          placeholder={searchPlaceholder}
          value={searchValue}
          onChange={(e) => onSearchChange(e.currentTarget.value)}
          style={{ flex: 1, minWidth: 0 }}
          leftSection={<IconSearch size={searchIconSize} color="#FF9800" />}
          rightSection={
            searchValue ? (
              <ActionIcon
                size="xs"
                variant="subtle"
                color="gray"
                onClick={() => onSearchChange("")}
              >
                <IconX size={14} />
              </ActionIcon>
            ) : (
              debouncing && <Loader size="xs" color="orange" />
            )
          }
          styles={{
            input: {
              backgroundColor: "rgba(255, 255, 255, 0.05)",
              border: "1px solid rgba(255, 152, 0, 0.3)",
              color: "#F0F0F0",
              fontFamily: "'Space Grotesk', sans-serif",
              borderRadius: "8px",
              fontSize: isDesktop ? undefined : "16px", // Minimum 16px on mobile to prevent zoom
              "&::placeholder": { color: "#B8B8B8" },
              "&:focus": { borderColor: "#FF9800" },
            },
          }}
        />

        <Box pos="relative" style={{ flexShrink: 0 }}>
          <ActionIcon
            variant="light"
            size={finalFilterButtonSize}
            color="orange"
            onClick={onFilterClick}
            style={{
              backgroundColor: "rgba(255, 152, 0, 0.1)",
              border: "1px solid rgba(255, 152, 0, 0.3)",
              borderRadius: "8px",
            }}
            aria-label={filterButtonAriaLabel}
          >
            <IconAdjustmentsHorizontal size={finalFilterIconSize} />
          </ActionIcon>
          {activeFilterCount > 0 && (
            <L2KBadge
              variant="accent"
              size="sm"
              radius="xl"
              style={{
                position: "absolute",
                top: -10,
                right: -10,
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
              }}
            >
              {activeFilterCount}
            </L2KBadge>
          )}
        </Box>
      </div>
    </Box>
  );
}

export default SearchFilterBar;
